import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import HotelSelector from "components/HotelSelector";
import Snackbar from "components/Snackbar";
import NoResults from "components/NoResult";

import { FaPlus, FaExclamationTriangle, FaChevronDown } from "react-icons/fa";

import useCommon from "hooks/useCommon";

import RequestCard from "./components/RequestCard";
import CreateRequestDialog from "./components/CreateRequestDialog";
import RequestDetailDialog from "./components/RequestDetailDialog";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import { createRequest, getRequests, resetCreateRequestState } from "store/slice/requestSlice";
import CardSkeleton from "components/Skeletons/CardSkeleton";
import UpdateRequestDialog from "./components/UpdateRequestDialog";
import { resetUpdateRequestState } from "store/slice/requestSlice";
import { updateRequest } from "store/slice/requestSlice";
import { bulkCompleteRequests } from "store/slice/requestSlice";
import { resetBulkCompleteState } from "store/slice/requestSlice";
import { softDeleteRequest } from "store/slice/requestSlice";
import usePermissions from "hooks/usePermissions";

const statusMap = {
  All: "all",
  Pending: "pending",
  "In Progress": "in_progress",
  Completed: "completed",
};

const Requests = () => {
  const { DISPATCH, STORE } = useCommon();
  const { can } = usePermissions();
  const { black, gradients, light } = colors;

  const hotelIds = STORE((state) => state.ui.hotelIds);
  const { requests, total, listLoading, listError, updateRequestSuccess, bulkCompleteSuccess } =
    STORE((state) => state.requests);

  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filter, setFilter] = useState("Pending");
  const [page, setPage] = useState(1);

  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [snackbar, setSnackbar] = useState({
    show: false,
    color: "success",
    title: "",
    content: "",
    icon: "check",
  });

  const showSnackbar = (data) => setSnackbar({ show: true, ...data });
  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, show: false }));

  const pageSize = 6;
  const hasMore = requests.length < total;

  const loadMore = async () => {
    if (loadingMore || listLoading || requests.length >= total) return;

    setLoadingMore(true);

    const nextPage = page + 1;

    try {
      const res = await DISPATCH(
        getRequests({
          hotelIds,
          status: statusMap[filter],
          search: debouncedSearch || "",
          page: nextPage,
          pageSize,
          sortBy: "due",
          sortDir: "asc",
        })
      ).unwrap();

      const newItems = res?.data || [];

      if (newItems.length > 0) {
        setPage(nextPage);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!hotelIds?.length) return;
    if (debouncedSearch && debouncedSearch.length < 3) return;

    const fetchRequests = async () => {
      setRefreshLoading(true);

      setPage(1);

      try {
        await DISPATCH(
          getRequests({
            hotelIds,
            status: statusMap[filter],
            search: debouncedSearch || "",
            page: 1,
            pageSize: 6,
            sortBy: "due",
            sortDir: "asc",
          })
        );
      } finally {
        setRefreshLoading(false);
      }
    };

    fetchRequests();
  }, [hotelIds, filter, debouncedSearch, updateRequestSuccess, bulkCompleteSuccess]);

  // useEffect(() => {
  //   if (!hotelIds?.length) return;
  //   if (debouncedSearch && debouncedSearch.length < 3) return;

  //   setPage(1);
  //   setHasMore(true);

  //   DISPATCH(
  //     getRequests({
  //       hotelIds,
  //       status: statusMap[filter],
  //       search: debouncedSearch || "",
  //       page: 1,
  //       pageSize: 6,
  //       sortBy: "due",
  //       sortDir: "asc",
  //     })
  //   );
  // }, [hotelIds, filter, debouncedSearch, updateRequestSuccess, bulkCompleteSuccess]);

  useEffect(() => {
    if (!loadMoreRef.current) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];

        if (first.isIntersecting) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: "250px",
        threshold: 0,
      }
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [loadMore]);

  const handleCreateRequest = async (payload) => {
    try {
      const res = await DISPATCH(createRequest(payload)).unwrap();

      DISPATCH(resetCreateRequestState());

      showSnackbar({
        color: "success",
        title: "Request Created",
        content: res?.message || "Request created successfully",
        icon: "check",
      });

      setCreateOpen(false);

      DISPATCH(
        getRequests({
          hotelIds,
          status: filter === "All" ? "all" : filter.toLowerCase(),
          search: debouncedSearch || "",
          page: 1,
          pageSize: 20,
          sortBy: "due",
          sortDir: "asc",
        })
      );
    } catch (err) {
      showSnackbar({
        color: "error",
        title: "Error",
        content: err || "Failed to create request",
        icon: "warning",
      });
    }
  };

  const handleUpdateRequest = async (payload) => {
    try {
      const res = await DISPATCH(
        updateRequest({
          requestId: payload.requestId,
          payload: {
            reservationId: selectedRequest?.reservationId,
            title: payload.title,
            description: payload.description,
            status: payload.status,
            assignedToUserIds: payload.assignedToUserIds,
            dueDate: payload.dueDate,
            dueTime: payload.dueTime,
          },
        })
      ).unwrap();

      DISPATCH(resetUpdateRequestState());

      showSnackbar({
        color: "success",
        title: "Request Updated",
        content: res?.message || "Request updated successfully",
        icon: "check",
      });

      setUpdateOpen(false);
    } catch (err) {
      showSnackbar({
        color: "error",
        title: "Error",
        content: err || "Failed to update request",
        icon: "warning",
      });
    }
  };

  const handleToggleComplete = async (request) => {
    try {
      const res = await DISPATCH(
        bulkCompleteRequests({
          requestIds: [request.id],
        })
      ).unwrap();

      if (res?.success) {
        DISPATCH(resetBulkCompleteState());

        showSnackbar({
          color: "success",
          title: "Request Completed",
          content: "Request marked as completed successfully",
          icon: "check",
        });
        return true;
      }
      return false;
    } catch (err) {
      showSnackbar({
        color: "error",
        title: "Error",
        content: err || "Failed to complete request",
        icon: "warning",
      });
      return false;
    }
  };

  const handleDeleteRequest = async (request) => {
    try {
      const res = await DISPATCH(softDeleteRequest(request.id)).unwrap();

      if (res?.success) {
        showSnackbar({
          color: "success",
          title: "Request Deleted",
          content: "Request deleted successfully",
          icon: "check",
        });

        return true;
      }

      return false;
    } catch (err) {
      showSnackbar({
        color: "error",
        title: "Error",
        content: err || "Failed to delete request",
        icon: "warning",
      });

      return false;
    }
  };

  const formattedRequests = requests.map((req) => ({
    id: req._id,
    reservationId: req.reservationId,
    guestName: req.guestName,
    room: req.roomNumber || req.roomName,
    title: req.title,
    description: req.description,

    status:
      req.status === "pending"
        ? "Pending"
        : req.status === "completed"
          ? "Completed"
          : "In Progress",

    deadline: req.dueDate
      ? req.dueTime
        ? new Date(`${req.dueDate.slice(0, 10)}T${req.dueTime}`)
        : new Date(req.dueDate)
      : null,

    dueDate: req.dueDate || null,
    dueTime: req.dueTime || null,

    assignedTo: req.assignedToUsers || [],
    createdBy: req.createdBy,
    createdAt: req.createdAt,
  }));

  const isEmpty = !listLoading && formattedRequests.length === 0;
  const showInitialLoader = listLoading && page === 1 && requests.length === 0;

  const showSkeletons = (listLoading && page === 1 && requests.length === 0) || refreshLoading;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <VuiBox py={1}>
        <Card>
          <VuiBox>
            <VuiBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              mb={2}
            >
              <VuiTypography variant="lg">Guest Requests</VuiTypography>

              <VuiBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
                <HotelSelector />

                <VuiBox sx={{ width: 220 }}>
                  <VuiInput
                    gradient
                    placeholder="Search requests..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </VuiBox>

                {can("createRequests") && (
                  <VuiButton
                    variant="contained"
                    color="primary"
                    onClick={() => setCreateOpen(true)}
                  >
                    <FaPlus size={12} style={{ marginRight: 6 }} />
                    Create Request
                  </VuiButton>
                )}
              </VuiBox>
            </VuiBox>

            <VuiBox display="flex" gap={1.5} mb={3}>
              {["Pending", "In Progress", "Completed", "All"].map((item) => (
                <VuiButton
                  key={item}
                  size="small"
                  variant={filter === item ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setFilter(item)}
                >
                  {item}
                </VuiButton>
              ))}
            </VuiBox>

            {listError && (
              <VuiBox
                mb={2}
                p={2}
                display="flex"
                alignItems="center"
                gap={1}
                borderRadius="lg"
                sx={{
                  border: "1px solid rgba(255,0,0,0.3)",
                  background: "rgba(255,0,0,0.05)",
                }}
              >
                <FaExclamationTriangle size={14} color="#ef4444" />
                <VuiTypography variant="caption" color="error">
                  {listError}
                </VuiTypography>
              </VuiBox>
            )}

            {!listError && (
              <VuiBox
                display="grid"
                gridTemplateColumns={{
                  xs: "1fr",
                  md: "1fr 1fr",
                  lg: "1fr 1fr 1fr",
                }}
                gap={2}
              >
                {showSkeletons ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <CardSkeleton key={i} lines={4} hasHeader hasChips hasFooter />
                  ))
                ) : (
                  <>
                    {formattedRequests.map((item) => (
                      <RequestCard
                        key={item.id}
                        request={item}
                        onView={() => {
                          setSelectedRequest(item);
                          setDetailOpen(true);
                        }}
                        onUpdate={() => {
                          setSelectedRequest(item);
                          setUpdateOpen(true);
                        }}
                        onDelete={() => handleDeleteRequest(item)}
                        onToggleComplete={handleToggleComplete}
                      />
                    ))}
                  </>
                )}
              </VuiBox>
            )}
            {/* Load More Skeleton */}
            {loadingMore && (
              <VuiBox
                mt={2}
                display="grid"
                gridTemplateColumns={{
                  xs: "1fr",
                  md: "1fr 1fr",
                  lg: "1fr 1fr 1fr",
                }}
                gap={2}
              >
                {Array.from({ length: 3 }).map((_, i) => (
                  <CardSkeleton key={i} lines={4} hasHeader hasChips hasFooter />
                ))}
              </VuiBox>
            )}

            {/* Load More Button */}
            {!listLoading &&
              formattedRequests.length > 0 &&
              requests.length < total &&
              !loadingMore && (
                <VuiBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                  mt={3}
                  onClick={loadMore}
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    width: "fit-content",
                    mx: "auto",
                    opacity: 0.9,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      opacity: 1,
                      transform: "translateY(1px)",
                    },
                  }}
                >
                  <VuiTypography
                    variant="caption"
                    sx={{
                      // fontWeight: 500,
                      letterSpacing: "0.3px",
                    }}
                  >
                    Load More Requests
                  </VuiTypography>

                  <FaChevronDown
                    size={12}
                    style={{
                      marginTop: "1px",
                    }}
                  />
                </VuiBox>
              )}

            {/* End Message */}
            {!listLoading &&
              formattedRequests.length > 0 &&
              total > pageSize &&
              requests.length >= total && (
                <VuiBox textAlign="center" mt={3}>
                  <VuiTypography variant="caption">No more requests available</VuiTypography>
                </VuiBox>
              )}

            {isEmpty && !loadingMore && <NoResults title="No requests found" />}
          </VuiBox>
        </Card>
      </VuiBox>

      {createOpen && (
        <CreateRequestDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreate={handleCreateRequest}
        />
      )}

      {updateOpen && (
        <UpdateRequestDialog
          open={updateOpen}
          onClose={() => setUpdateOpen(false)}
          request={selectedRequest}
          onUpdate={handleUpdateRequest}
        />
      )}

      <RequestDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        request={selectedRequest}
        onToggleComplete={handleToggleComplete}
        logs={[]}
      />

      <Snackbar
        color={snackbar.color}
        icon={snackbar.icon}
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.show}
        onClose={closeSnackbar}
        close={closeSnackbar}
      />
    </DashboardLayout>
  );
};

export default Requests;
